import React, { useState, useEffect } from 'react';
import { User, Mail, Lock, Phone, Calendar, School, Users, Trophy, UserCheck } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const EnhancedRegistration = ({ onRegistrationSuccess, onSwitchToLogin }) => {
  const [loading, setLoading] = useState(false);
  const [teams, setTeams] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone_number: '',
    birthdate: '',
    role: '',
    sport: '',
    gender: '',
    level: '',
    school_club_name: '',
    team_name: '',
    requested_team_id: ''
  });
  const { toast } = useToast();

  const userTypes = [
    { value: 'athlete', label: 'Athlete', description: 'Student athlete participating in sports' },
    { value: 'coach', label: 'Coach', description: 'Team coach or assistant coach' },
    { value: 'team-admin', label: 'Team Administrator', description: 'Team administrator managing team operations' },
    { value: 'sport-performance-consultant', label: 'Sport Mental Performance Consultant', description: 'Professional sport psychology consultant' },
    { value: 'school-admin', label: 'School/Club Administrator', description: 'School or club administrator overseeing multiple teams' },
    { value: 'system-admin', label: 'System Administrator', description: 'System administrator with full access' }
  ];

  const sports = [
    'Basketball', 'Football', 'Soccer', 'Baseball', 'Softball', 'Tennis', 
    'Track & Field', 'Swimming', 'Volleyball', 'Golf', 'Cross Country', 
    'Wrestling', 'Lacrosse', 'Field Hockey', 'Other'
  ];

  const levels = [
    'Varsity', 'Junior Varsity', 'Sophomore', 'Freshman', 'Club', 'Recreational'
  ];

  useEffect(() => {
    if (formData.role === 'athlete' && formData.sport && formData.school_club_name) {
      fetchAvailableTeams();
    }
  }, [formData.role, formData.sport, formData.school_club_name]);

  const fetchAvailableTeams = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/teams`);
      
      if (response.ok) {
        const allTeams = await response.json();
        // Filter teams based on sport and school (basic filtering)
        const relevantTeams = allTeams.filter(team => 
          team.sport === formData.sport || 
          team.name.toLowerCase().includes(formData.sport.toLowerCase()) ||
          team.name.toLowerCase().includes(formData.school_club_name.toLowerCase())
        );
        setTeams(relevantTeams);
      }
    } catch (error) {
      console.error('Error fetching teams:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.email.trim() || !formData.password.trim() || !formData.phone_number.trim() || !formData.role) {
      toast({
        title: 'Error',
        description: 'Please fill in all required fields',
        variant: 'destructive'
      });
      return;
    }

    if (!formData.birthdate) {
      toast({
        title: 'Error',
        description: 'Please provide your birthdate',
        variant: 'destructive'
      });
      return;
    }

    if (!formData.school_club_name.trim()) {
      toast({
        title: 'Error',
        description: 'Please provide your school or club name',
        variant: 'destructive'
      });
      return;
    }

    // Sport is required for all roles except system-admin and school-admin
    const sportRequiredRoles = ['athlete', 'coach', 'team-admin', 'sport-performance-consultant'];
    if (sportRequiredRoles.includes(formData.role) && !formData.sport) {
      toast({
        title: 'Error',
        description: 'Please select your sport',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);

    try {
      const submitData = { ...formData };
      
      // Only include team request for athletes
      if (formData.role !== 'athlete') {
        delete submitData.requested_team_id;
        delete submitData.team_name;
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submitData)
      });

      if (response.ok) {
        const result = await response.json();
        
        let successMessage = 'Registration successful!';
        if (formData.role === 'athlete' && formData.requested_team_id) {
          successMessage += ' Your team join request has been sent to the coach and team administrator for approval.';
        }
        
        toast({
          title: 'Registration Successful',
          description: successMessage
        });
        
        // Store token and user data
        localStorage.setItem('token', result.token);
        localStorage.setItem('user', JSON.stringify(result.user));
        
        onRegistrationSuccess && onRegistrationSuccess(result.user);
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Registration failed');
      }
    } catch (error) {
      console.error('Registration error:', error);
      toast({
        title: 'Registration Failed',
        description: error.message || 'Please try again',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-emerald-50 flex items-center justify-center p-6">
      <div className="w-full max-w-2xl bg-white rounded-lg shadow-xl overflow-hidden">
        <div className="bg-emerald-600 px-8 py-6">
          <h2 className="text-2xl font-bold text-white text-center">Create Your Account</h2>
          <p className="text-emerald-100 text-center mt-2">Join the PeakMind sport psychology platform</p>
        </div>

        <form onSubmit={handleSubmit} className="px-8 py-6 space-y-6">
          {/* Personal Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Full Name *
              </label>
              <div className="relative">
                <User className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="Enter your full name"
                  required
                  disabled={loading}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address *
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="Enter your email"
                  required
                  disabled={loading}
                />
              </div>
            </div>
          </div>

          {/* Password and Phone Number */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password *
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="Create a secure password"
                  required
                  disabled={loading}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number *
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <input
                  type="tel"
                  value={formData.phone_number}
                  onChange={(e) => handleInputChange('phone_number', e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="Enter your phone number"
                  required
                  disabled={loading}
                />
              </div>
            </div>
          </div>

          {/* Birthdate */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Birthdate *
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <input
                type="date"
                value={formData.birthdate}
                onChange={(e) => handleInputChange('birthdate', e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                required
                disabled={loading}
              />
            </div>
          </div>

          {/* User Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Account Type *
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {userTypes.map((type) => (
                <label
                  key={type.value}
                  className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                    formData.role === type.value
                      ? 'border-emerald-500 bg-emerald-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="role"
                    value={type.value}
                    checked={formData.role === type.value}
                    onChange={(e) => handleInputChange('role', e.target.value)}
                    className="sr-only"
                    disabled={loading}
                  />
                  <div className="font-medium text-gray-900">{type.label}</div>
                  <div className="text-sm text-gray-600 mt-1">{type.description}</div>
                </label>
              ))}
            </div>
          </div>

          {/* School/Club Information */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              School/Club Name *
            </label>
            <div className="relative">
              <School className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={formData.school_club_name}
                onChange={(e) => handleInputChange('school_club_name', e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder="Enter your school or club name"
                required
                disabled={loading}
              />
            </div>
          </div>

          {/* Sport and Athletic Information */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sport {!['system-admin', 'school-admin'].includes(formData.role) && '*'}
              </label>
              <select
                value={formData.sport}
                onChange={(e) => handleInputChange('sport', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                required={!['system-admin', 'school-admin'].includes(formData.role)}
                disabled={loading}
              >
                <option value="">Select sport</option>
                {sports.map(sport => (
                  <option key={sport} value={sport}>{sport}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Gender
              </label>
              <select
                value={formData.gender}
                onChange={(e) => handleInputChange('gender', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                disabled={loading}
              >
                <option value="">Select gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="non-binary">Non-binary</option>
                <option value="prefer-not-to-say">Prefer not to say</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Level
              </label>
              <select
                value={formData.level}
                onChange={(e) => handleInputChange('level', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                disabled={loading}
              >
                <option value="">Select level</option>
                {levels.map(level => (
                  <option key={level} value={level}>{level}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Team Selection for Athletes */}
          {formData.role === 'athlete' && teams.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Request to Join Team (Optional)
              </label>
              <div className="relative">
                <Users className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <select
                  value={formData.requested_team_id}
                  onChange={(e) => {
                    const team = teams.find(t => t.id === e.target.value);
                    handleInputChange('requested_team_id', e.target.value);
                    handleInputChange('team_name', team ? team.name : '');
                  }}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  disabled={loading}
                >
                  <option value="">No team request (join later)</option>
                  {teams.map(team => (
                    <option key={team.id} value={team.id}>
                      {team.name} ({team.sport}) - {team.athlete_ids?.length || 0} athletes
                    </option>
                  ))}
                </select>
              </div>
              {formData.requested_team_id && (
                <p className="text-sm text-blue-600 mt-2">
                  📧 A request will be sent to the coach and team administrator for approval
                </p>
              )}
            </div>
          )}

          {/* Submit Button */}
          <div className="pt-4">
            <button
              type="submit"
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 px-4 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              disabled={loading}
            >
              {loading && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              )}
              <UserCheck className="w-4 h-4" />
              <span>{loading ? 'Creating Account...' : 'Create Account'}</span>
            </button>
          </div>

          {/* Switch to Login */}
          <div className="text-center pt-4 border-t border-gray-200">
            <p className="text-sm text-gray-600">
              Already have an account?{' '}
              <button
                type="button"
                onClick={onSwitchToLogin}
                className="text-emerald-600 hover:text-emerald-700 font-medium"
                disabled={loading}
              >
                Sign in here
              </button>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EnhancedRegistration;